import React, { CSSProperties, KeyboardEvent, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { CFieldValidation, FieldValidation } from '@/src/components/c-field-validations/CFieldValidation';
import { guid } from '@/src/infrastructure/guid/guid';
import { sanitize } from '@/src/infrastructure/sanitize/sanitize';
import { CSeverity } from '@/src/components/interfaces';
import { CFlex, FlexDirection } from '@/src/components/c-flex/CFlex';
import { CBox } from '@/src/components/c-box/CBox';

import { CFieldLabel, CFieldSecurity } from '@/src/components/c-field-label/CFieldLabel';

export type CFieldEvent = {
  target: any;
  type?: any;
};

export type CFieldChangeCallback = (event: CFieldEvent, value: any) => (Promise<void | boolean> | void | boolean);

export enum CFieldInputMode {
  Text = 'text',
  Email = 'email',
  Tel = 'tel',
  Url = 'url',
  Numeric = 'numeric',
  Decimal = 'decimal',
  Search = 'search',
  None = 'none',
}

export enum CFieldEnterKeyHint {
  Enter = 'enter',
  Done = 'done',
  Go = 'go',
  Next = 'next',
  Previous = 'previous',
  Search = 'search',
  Send = 'send',
}

export enum CFieldAutoCapitalize {
  Off = 'off',
  None = 'none',
  On = 'on',
  Sentences = 'sentences',
  Words = 'words',
  Characters = 'characters',
}

export enum CFieldAutoCorrect {
  Off = 'off',
  On = 'on',
}

export interface CFieldProps {
  id?: string;
  class?: string;
  label?: string;
  value?: string;
  style?: CSSProperties;
  placeholder?: string;
  testSelector?: string;
  validations?: FieldValidation[];
  onChange?: CFieldChangeCallback;
  onBlur?: (event: CFieldEvent, value?: any) => (Promise<boolean | void> | boolean | void);
  onFocus?: (event: CFieldEvent, value?: any) => (Promise<boolean | void> | boolean | void);
  onKeyUp?: (event: KeyboardEvent) => (Promise<void> | void);
  onKeyDown?: (event: KeyboardEvent) => (Promise<void> | void);
  ref?: (instance: any) => void;
  name?: string;
  min?: string | number;
  max?: string | number;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  required?: boolean;
  disabled?: boolean;
  icon?: boolean;
  security?: CFieldSecurity;
  formId?: string;
  autoComplete?: string;
  hideMessageContainer?: boolean;
  fullWidth?: boolean;
}

interface DynamicProps {
  id: string;
  value: string;
  setValue: CFieldChangeCallback;
}

interface PrivateProps {
  children: (props: DynamicProps) => React.JSX.Element;
}

export function CField(props: CFieldProps & PrivateProps): React.JSX.Element {
  const [value, setValue] = useState<string>(sanitize(props.value ?? ''));
  const [id] = useState(props.id ?? guid('field'));
  const validations = props.validations ?? [];

  const onChange = useCallback(async (event: CFieldEvent, value: string) => {
    if (!props.disabled) {
      const sanitized = sanitize(value);
      setValue(() => sanitized);
      await props.onChange?.(event, sanitized);
    }
  }, [props]);

  const status = useMemo(() => {
    const hasErrors = props.validations?.some(error => error.severity === CSeverity.Error) ?? false;
    const hasWarnings = props.validations?.some(error => error.severity === CSeverity.Warning) ?? false;

    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';

    return 'neutral';
  }, [props]);

  return (
    <CFlex
      id={props.id}
      class={classNames('c-field', 'c-gap--xs', props.class, {
        'c-field--disabled': !!props.disabled,
        'c-field--full-width': !!props.fullWidth,
        [`c-field--status-${status}`]: status,
      })}
      direction={FlexDirection.Column}
      data-test-selector={props.testSelector}
      style={props.style}
    >
      {(props.label?.trim().length ?? 0) > 0 && (
        <CFieldLabel
          htmlFor={`${id}-input`}
          formId={props.formId}
          label={props.label!}
          required={props.required}
          security={props.security}
          hasValue={value.trim() !== ''}
          hasPlaceholder={props.placeholder !== undefined}
        />
      )}
      <CBox class={classNames('c-field__container', {
        'c-field__container--iconic': props.icon,
      })}>
        {props.children({ id: `${id}-input`, value, setValue: onChange })}
      </CBox>
      {validations.length > 0 && !props.hideMessageContainer && (
        <CFieldValidation validations={validations} testSelector="field-validation"/>
      )}
    </CFlex>
  );
}
